import { DataManager } from './DataManager';

export class CalendarInfoModal {
  private modal: HTMLElement;
  private calendarInfoHeader: HTMLInputElement;
  private titleInput: HTMLInputElement;
  private birthdateInput: HTMLInputElement;
  private ageInput: HTMLInputElement;
  private saveBtn: HTMLButtonElement;
  private cancelBtn: HTMLButtonElement;
  private moveUpBtn: HTMLButtonElement;
  private moveDownBtn: HTMLButtonElement;
  private backdrop: HTMLElement;
  private publishSection: HTMLElement;
  private publishedSection: HTMLElement;
  private publishBtn: HTMLButtonElement;
  private publicLinkInput: HTMLInputElement;
  private copyLinkBtn: HTMLButtonElement;
  private unpublishBtn: HTMLButtonElement;
  private unpublishBtnDefaultText: string;
  private dataManager: DataManager;
  private onSuccess?: () => void;
  private onMove?: () => void;
  private editingId: string = ''; // empty means creating new, non-empty means editing existing
  private copyFromId: string = ''; // empty means not copying, non-empty means copying from this id

  constructor(dataManager: DataManager) {
    this.dataManager = dataManager;
    
    this.backdrop = document.getElementById('calendarInfoBackdrop') as HTMLElement;
    this.modal = document.getElementById('calendarInfoModal') as HTMLElement;
    this.calendarInfoHeader = document.getElementById('calendarInfoHeader') as HTMLInputElement;
    this.titleInput = document.getElementById('calendarTitle') as HTMLInputElement;
    this.birthdateInput = document.getElementById('calendarBirthdate') as HTMLInputElement;
    this.ageInput = document.getElementById('calendarAge') as HTMLInputElement;
    this.saveBtn = document.getElementById('saveCalendarBtn') as HTMLButtonElement;
    this.cancelBtn = document.getElementById('cancelCalendarBtn') as HTMLButtonElement;
    this.moveUpBtn = document.getElementById('moveCalendarUpBtn') as HTMLButtonElement;
    this.moveDownBtn = document.getElementById('moveCalendarDownBtn') as HTMLButtonElement;
    this.publishSection = document.getElementById('publishSection') as HTMLElement;
    this.publishedSection = document.getElementById('publishedSection') as HTMLElement;
    this.publishBtn = document.getElementById('publishCalendarBtn') as HTMLButtonElement;
    this.publicLinkInput = document.getElementById('publicLink') as HTMLInputElement;
    this.copyLinkBtn = document.getElementById('copyPublicLinkBtn') as HTMLButtonElement;
    this.unpublishBtn = document.getElementById('unpublishCalendarBtn') as HTMLButtonElement;
    this.unpublishBtnDefaultText = this.unpublishBtn.innerText;
    
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.saveBtn.addEventListener('click', () => {
      this.handleSave();
    });

    this.cancelBtn.addEventListener('click', () => {
      this.hide();
    });

    this.moveUpBtn.addEventListener('click', () => {
      this.handleMoveCalendar(-1);
    });

    this.moveDownBtn.addEventListener('click', () => {
      this.handleMoveCalendar(1);
    });

    this.backdrop.addEventListener('click', () => {
      this.hide();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.hide();
      }
    });

    const form = this.modal.querySelector('#calendarInfoForm') as HTMLFormElement;
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSave();
      }
    });

    this.publishBtn.addEventListener('click', () => {
      this.handlePublish();
    });

    this.unpublishBtn.addEventListener('click', () => {
      this.handleUnpublish();
    });

    this.copyLinkBtn.addEventListener('click', () => {
      this.handleCopyLink();
    });
  }

  private async handleSave(): Promise<void> {
    if (this.saveBtn.disabled) {
      return;
    }

    const title = this.titleInput.value.trim();
    const birthdate = this.birthdateInput.value;
    const age = parseInt(this.ageInput.value);

    this.saveBtn.disabled = true;
    this.saveBtn.textContent = 'Saving...';

    try {
      let success: boolean;
      if (this.editingId) {
        success = this.dataManager.updateCalendar(this.editingId, title, birthdate, age);
      } else if (this.copyFromId) {
        success = this.dataManager.copyCalendar(this.copyFromId, title, birthdate, age);
      } else {
        success = this.dataManager.createNewCalendar(title, birthdate, age);
      }
      
      if (success) {
        this.onSuccess && this.onSuccess();
        this.hide();
      }
    } finally {
      this.saveBtn.disabled = false;
      this.saveBtn.textContent = 'Save';
    }
  }

  private handleMoveCalendar(shift: number): void {
    if (this.editingId) {
      this.dataManager.moveCalendar(this.editingId, shift);
      const calendars = this.dataManager.getCalendars();
      const index = calendars.findIndex(c => c.id === this.editingId);
      this.moveUpBtn.disabled = index === 0;
      this.moveDownBtn.disabled = index === calendars.length - 1;
      if (this.onMove)  this.onMove()
    }
  }

  private async handlePublish(): Promise<void> {
    if (!this.editingId) return;
    if (this.publishBtn.disabled) return;

    this.publishBtn.disabled = true;
    this.publishBtn.textContent = 'Publishing...';

    try {
      const success = await this.dataManager.publishCalendar(this.editingId);
      if (success) {
        this.updatePublishUI();
      }
    } finally {
      this.publishBtn.disabled = false;
      this.publishBtn.textContent = 'Publish to web';
    }
  }

  private async handleUnpublish(): Promise<void> {
    if (!this.editingId) return;
    if (this.unpublishBtn.disabled) return;

    this.unpublishBtn.disabled = true;
    this.unpublishBtn.textContent = 'Unpublishing...';

    try {
      const success = await this.dataManager.unpublishCalendar(this.editingId);
      if (success) {
        this.updatePublishUI();
      }
    } finally {
      this.unpublishBtn.disabled = false;
      this.unpublishBtn.textContent = this.unpublishBtnDefaultText;
    }
  }

  private handleCopyLink(): void {
    if (this.publicLinkInput.value) {
      this.publicLinkInput.select();
      this.publicLinkInput.setSelectionRange(0, 99999); // For mobile devices
      navigator.clipboard.writeText(this.publicLinkInput.value).then(() => {
        // Visual feedback - could be improved with a toast notification
        const originalText = this.copyLinkBtn.textContent;
        this.copyLinkBtn.textContent = '✓';
        setTimeout(() => {
          this.copyLinkBtn.textContent = originalText;
        }, 1000);
      }).catch(() => {
        // Fallback for older browsers
        document.execCommand('copy');
      });
    }
  }

  private updatePublishUI(): void {
    if (!this.editingId) {
      this.publishSection.style.display = 'none';
      this.publishedSection.style.display = 'none';
      return;
    }

    // Only show publish options if user is logged in
    if (!this.dataManager.isLoggedIn()) {
      this.publishSection.style.display = 'none';
      this.publishedSection.style.display = 'none';
      return;
    }

    const publicLink = this.dataManager.getPublicLink(this.editingId);
    if (publicLink) {
      this.publishSection.style.display = 'none';
      this.publishedSection.style.display = 'block';
      this.publicLinkInput.value = publicLink;
    } else {
      this.publishSection.style.display = 'block';
      this.publishedSection.style.display = 'none';
    }
  }

  public show(onSuccess?: () => void): void {
    this.editingId = '';
    this.copyFromId = '';
    this.onSuccess = onSuccess;
    this.onMove = undefined;

    // Reset form
    this.calendarInfoHeader.innerText = "Add Calendar";
    this.titleInput.value = '';
    const { birthDate, age } = this.dataManager.getSelectedCalendarInfo();
    this.birthdateInput.value = birthDate.format('YYYY-MM-DD');
    this.ageInput.value = age.toString();
    
    // Hide move buttons when creating new calendar
    this.moveUpBtn.style.display = 'none';
    this.moveDownBtn.style.display = 'none';
    
    // Hide publish sections when creating new calendar
    this.publishSection.style.display = 'none';
    this.publishedSection.style.display = 'none';
    
    // Show modal
    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';
    
    this.titleInput.focus();
  }

  public showForEdit(calendarId: string, onSuccess?: () => void, onMove?: () => void): void {
    this.editingId = calendarId;
    this.copyFromId = '';
    this.onSuccess = onSuccess;
    this.onMove = onMove;

    // Get calendar data for editing
    const calendars = this.dataManager.getCalendars();
    const calendar = calendars.find(c => c.id === calendarId)!;
    const index = calendars.findIndex(c => c.id === calendarId);
    
    // Populate form with existing data
    this.calendarInfoHeader.innerText = "Edit Calendar";
    this.titleInput.value = calendar.title || '';
    this.birthdateInput.value = calendar.birthdate;
    this.ageInput.value = calendar.age.toString();
    
    // Show move buttons when editing and enable/disable based on position
    this.moveUpBtn.style.display = 'inline-block';
    this.moveDownBtn.style.display = 'inline-block';
    this.moveUpBtn.disabled = index === 0;
    this.moveDownBtn.disabled = index === calendars.length - 1;
    
    // Update publish UI
    this.updatePublishUI();
    
    // Show modal
    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';
    
    this.titleInput.focus();
  }

  public showForCopy(calendarId: string, onSuccess?: () => void): void {
    this.editingId = ''; // ensure create mode
    this.copyFromId = calendarId;
    this.onSuccess = onSuccess;
    this.onMove = undefined;

    const calendars = this.dataManager.getCalendars();
    const calendar = calendars.find(c => c.id === calendarId)!;

    this.calendarInfoHeader.innerText = "Copy " + (calendar.title || "Calendar");
    this.titleInput.value = calendar.title || '';
    this.birthdateInput.value = calendar.birthdate;
    this.ageInput.value = calendar.age.toString();

    // Hide move buttons when creating new calendar (copy)
    this.moveUpBtn.style.display = 'none';
    this.moveDownBtn.style.display = 'none';

    // Hide publish sections when copying calendar
    this.publishSection.style.display = 'none';
    this.publishedSection.style.display = 'none';

    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';

    this.titleInput.focus();
  }

  public hide(): void {
    this.backdrop.style.display = 'none';
    this.modal.style.display = 'none';
    this.onSuccess = undefined;
    this.onMove = undefined;
    this.editingId = '';
    this.copyFromId = '';
    
    // Reset move button states just in case
    this.moveUpBtn.style.display = 'none';
    this.moveDownBtn.style.display = 'none';
    this.moveUpBtn.disabled = false;
    this.moveDownBtn.disabled = false;
    
    // Reset publish sections
    this.publishSection.style.display = 'none';
    this.publishedSection.style.display = 'none';
  }

  public isVisible(): boolean {
    return this.modal.style.display !== 'none';
  }
} 